import { useCallback, useEffect, useState, useMemo, Fragment } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import { useEntity } from "@/context/EntityContext";
import ConsolidatedRailCard from "@/components/ConsolidatedRailCard";
import DrillDownModal from "@/components/DrillDownModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  SelectGroup, SelectLabel,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  TrendingDown, TrendingUp, Scale, BookOpen, FileSpreadsheet,
  Download, Plus, Trash2, ArrowDownUp, Sparkles,
  CheckSquare, Square, ChevronDown, Building2, Tag, X, Users, FileText,
} from "lucide-react";
import { startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";
import { toast } from "sonner";
import { DateField, fmtISO, downloadStream, CsvButton, BalancedBadge, RailSectionHeader } from "./_shared";
import { LiabilitiesManager } from "./LiabilitiesManager";

export function BalanceSheetReport({ currency }) {
  const [asOf, setAsOf] = useState(new Date());
  const [rawData, setRawData] = useState(null);
  const [drill, setDrill] = useState(null);
  const [detailed, setDetailed] = useState(false);
  const { filterRails } = useCurrencyRail();
  const { currentEntity } = useEntity();
  const isBusiness = currentEntity?.workspace_type === "business";
  const load = useCallback(async () => {
    const { data } = await api.get("/accounting/balance-sheet", { params: { as_of: fmtISO(asOf) } });
    setRawData(data);
  }, [asOf]);
  useEffect(() => { load(); }, [load]);

  if (!rawData) return <div className="text-sm text-[var(--muted-text)]">Loading…</div>;
  const data = { ...rawData, by_currency: filterRails(rawData.by_currency || []) };

  const multi = Array.isArray(data.by_currency) && data.by_currency.length > 1;

  const row = (label, amount, ccy, indent = false, muted = false, drillParams = null) => (
    <tr
      key={label}
      data-testid={`bs-row-${(label || "").replace(/\s+/g, "-").toLowerCase()}`}
      onClick={drillParams ? () => setDrill({ title: `${label} · ${formatCurrency(amount, ccy)}`, params: drillParams }) : undefined}
      className={`border-b border-[var(--divider)] last:border-none ${drillParams ? "cursor-pointer hover:bg-[var(--row-hover)]" : ""}`}
    >
      <td className={`py-2 px-5 ${indent ? "pl-10" : ""} ${muted ? "text-[var(--muted-text)]" : ""}`}>{label}</td>
      <td className="py-2 px-5 text-right font-mono-data">{formatCurrency(amount, ccy)}</td>
    </tr>
  );

  // Renders one rail's Assets + Equity blocks. Liabilities are shown once
  // (primary rail only) via <LiabilitiesManager> outside this helper.
  const RailBlock = ({ rail }) => {
    const ccy = rail.currency;
    const inv = rail.assets.investments;
    return (
      <div data-testid={`bs-rail-${ccy}`} className="card-flat overflow-hidden border-l-4"
           style={{ borderLeftColor: ccy === "USD" ? "#1E5A96" : "#4C956C" }}>
        <div className="px-5 py-3 border-b border-[var(--divider)] flex items-baseline justify-between">
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">{ccy} rail</div>
          <BalancedBadge balanced={rail.balanced}
                         difference={rail.assets.total - (rail.liabilities.total + rail.equity.total)}
                         currency={ccy} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-x divide-[var(--divider)]">
          <table className="w-full text-sm">
            <tbody>
              <tr className="bg-[var(--hover)]">
                <td className="py-2 px-5 text-xs uppercase tracking-wider text-[var(--muted-text)]">Assets · Wallets</td>
                <td data-testid={`bs-wallets-total-${ccy}`} className="py-2 px-5 text-right font-mono-data">{formatCurrency(rail.assets.wallets.total, ccy)}</td>
              </tr>
              {rail.assets.wallets.lines.map((l) => row(l.name, l.balance, ccy, true, false,
                l.id ? { kind: "wallet", account_id: l.id, as_of: fmtISO(asOf) } : null))}
              {rail.assets.savings_buckets.lines.length > 0 && (
                <tr className="bg-[var(--hover)]">
                  <td className="py-2 px-5 text-xs uppercase tracking-wider text-[var(--muted-text)]">Savings buckets</td>
                  <td className="py-2 px-5 text-right font-mono-data">{formatCurrency(rail.assets.savings_buckets.total, ccy)}</td>
                </tr>
              )}
              {rail.assets.savings_buckets.lines.map((l) => row(l.name, l.balance, ccy, true, false,
                { kind: "savings_bucket", category: l.name, as_of: fmtISO(asOf) }))}
              {inv && inv.market_value > 0 && (
                <Fragment key={`inv-${ccy}`}>
                  <tr className="bg-[var(--hover)]">
                    <td className="py-2 px-5 text-xs uppercase tracking-wider text-[var(--muted-text)]">Investments</td>
                    <td className="py-2 px-5 text-right font-mono-data">{formatCurrency(inv.market_value, ccy)}</td>
                  </tr>
                  {row("At cost basis", inv.cost_basis, ccy, true, true)}
                </Fragment>
              )}
              <tr className="border-t-2 border-[#2F4F4F] font-semibold">
                <td className="py-3 px-5">Total assets</td>
                <td data-testid={`bs-total-assets-${ccy}`} className="py-3 px-5 text-right font-mono-data">{formatCurrency(rail.assets.total, ccy)}</td>
              </tr>
            </tbody>
          </table>
          <table className="w-full text-sm">
            <tbody>
              {rail.liabilities.total > 0 && (
                <>
                  <tr className="bg-[var(--hover)]">
                    <td className="py-2 px-5 text-xs uppercase tracking-wider text-[var(--muted-text)]">Liabilities</td>
                    <td className="py-2 px-5 text-right font-mono-data">{formatCurrency(rail.liabilities.total, ccy)}</td>
                  </tr>
                  {rail.liabilities.lines.map((l) => row(l.name, l.balance, ccy, true))}
                </>
              )}
              <tr className="bg-[var(--hover)]">
                <td className="py-2 px-5 text-xs uppercase tracking-wider text-[var(--muted-text)]">Equity</td>
                <td className="py-2 px-5 text-right font-mono-data">{formatCurrency(rail.equity.total, ccy)}</td>
              </tr>
              {row("Owner's capital (opening)", rail.equity.owners_capital, ccy, true)}
              {row("Retained earnings", rail.equity.retained_earnings, ccy, true)}
              {rail.equity.unrealized_investment_gains !== 0 && row("Unrealized investment gains", rail.equity.unrealized_investment_gains, ccy, true)}
              {rail.equity.investment_capital_introduced !== 0 && row("Investment capital introduced", rail.equity.investment_capital_introduced, ccy, true)}
              {rail.equity.borrowed_capital_offset !== 0 && row("Less: borrowed capital", rail.equity.borrowed_capital_offset, ccy, true)}
              <tr className="border-t-2 border-[#2F4F4F] font-semibold">
                <td className="py-3 px-5">Liabilities + Equity</td>
                <td data-testid={`bs-liab-plus-equity-${ccy}`} className="py-3 px-5 text-right font-mono-data">{formatCurrency(rail.liabilities.total + rail.equity.total, ccy)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="bs-report" className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <DateField label="As of" value={asOf} onChange={setAsOf} testId="bs-asof" />
        <div className="flex items-center gap-2 flex-wrap">
          {isBusiness && (
            <label className="inline-flex items-center gap-2 text-xs text-[var(--muted-text)] px-2 py-1 rounded-full border border-[var(--divider)]"
                   data-testid="bs-detailed-toggle-label">
              <Switch checked={detailed} onCheckedChange={setDetailed}
                       data-testid="bs-detailed-toggle" />
              <span>Detailed view</span>
            </label>
          )}
          {multi ? (
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${
              data.by_currency.every((r) => r.balanced) ? "bg-[#EAF4EE] text-[#2F6B4A]" : "bg-[#FCE7E9] text-[#B4283A]"
            }`}>
              {data.by_currency.every((r) => r.balanced) ? "All rails balanced" : "One or more rails off"}
            </span>
          ) : (
            <BalancedBadge balanced={data.balanced} difference={data.assets.total - (data.liabilities.total + data.equity.total)} currency={currency} />
          )}
          <CsvButton testId="bs-csv" onClick={() => downloadStream("/accounting/balance-sheet.csv", { as_of: fmtISO(asOf) }, `balance_sheet_${fmtISO(asOf)}.csv`)} />
          {isBusiness && (
            <Button data-testid="bs-pdf-btn" variant="outline" size="sm"
                     onClick={() => downloadStream("/accounting/balance-sheet.pdf",
                                                   { as_of: fmtISO(asOf), detailed: detailed },
                                                   `balance_sheet_${fmtISO(asOf)}.pdf`)}
                     className="rounded-full">
              <FileText className="w-4 h-4 mr-1" /> PDF
            </Button>
          )}
        </div>
      </div>

      {multi ? (
        <>
          <ConsolidatedRailCard
            testId="bs-consolidated"
            subtitle="flip the sidebar FX toggle to hide"
            rails={data.by_currency.map((r) => ({
              currency: r.currency,
              assets: r.assets.total,
              liabilities: r.liabilities.total,
              equity: r.equity.total,
            }))}
            fields={[
              { key: "assets", label: "Total Assets" },
              { key: "liabilities", label: "Total Liabilities", color: "text-[#B4283A]" },
              { key: "equity", label: "Total Equity" },
            ]}
          />
          {/* One rail block per currency — Assets & Equity balance independently */}
          <div className="space-y-4">
            {data.by_currency.map((rail) => (
              <div key={rail.currency}>
                <RailSectionHeader
                  currency={rail.currency}
                  subtitle="Assets = Liabilities + Equity"
                  testId={`bs-rail-header-${rail.currency}`}
                  right={
                    <BalancedBadge
                      balanced={rail.balanced}
                      difference={rail.assets.total - (rail.liabilities.total + rail.equity.total)}
                      currency={rail.currency}
                    />
                  }
                />
                <RailBlock rail={rail} />
              </div>
            ))}
          </div>
          {/* Liabilities management stays global — liabilities are in primary currency */}
          <LiabilitiesManager currency={currency} onChange={load} />
        </>
      ) : (
        // Single-rail render. When the rail selector narrows a multi-rail
        // response down to one rail, we MUST render from that rail's own
        // per-currency numbers — not the top-level cross-currency totals
        // (iter 60m fix, cross-rail investment leak).
        (() => {
          const rail = (data.by_currency && data.by_currency[0]) || null;
          const railAssets = rail ? rail.assets : data.assets;
          const railEquity = rail ? rail.equity : data.equity;
          const railLiab = rail ? rail.liabilities : data.liabilities;
          const railCcy = rail ? rail.currency : currency;
          const investments = railAssets.investments || null;
          return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="card-flat overflow-hidden">
                <div className="px-5 py-3 border-b border-[var(--divider)] text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Assets</div>
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="bg-[var(--hover)]"><td className="py-2 px-5 text-xs uppercase tracking-wider text-[var(--muted-text)]">Wallets</td>
                      <td className="py-2 px-5 text-right font-mono-data">{formatCurrency(railAssets.wallets.total, railCcy)}</td></tr>
                    {railAssets.wallets.lines.map((l) => row(l.name, l.balance, railCcy, true, false,
                      l.id ? { kind: "wallet", account_id: l.id, as_of: fmtISO(asOf) } : null))}
                    {railAssets.savings_buckets.lines.length > 0 && (
                      <tr className="bg-[var(--hover)]"><td className="py-2 px-5 text-xs uppercase tracking-wider text-[var(--muted-text)]">Savings buckets</td>
                        <td className="py-2 px-5 text-right font-mono-data">{formatCurrency(railAssets.savings_buckets.total, railCcy)}</td></tr>
                    )}
                    {railAssets.savings_buckets.lines.map((l) => row(l.name, l.balance, railCcy, true, false,
                      { kind: "savings_bucket", category: l.name, as_of: fmtISO(asOf) }))}
                    {investments && investments.market_value > 0 && (
                      <Fragment key="inv">
                        <tr className="bg-[var(--hover)]"><td className="py-2 px-5 text-xs uppercase tracking-wider text-[var(--muted-text)]">Investments</td>
                          <td className="py-2 px-5 text-right font-mono-data">{formatCurrency(investments.market_value, railCcy)}</td></tr>
                        {row("At cost basis", investments.cost_basis, railCcy, true, true)}
                      </Fragment>
                    )}
                    {/* iter 72ag — Detailed Business Balance Sheet.
                        Shows every AR invoice, every AP bill, every SKU,
                        each fixed asset with cost/accum/book value. */}
                    {railAssets.accounts_receivable && railAssets.accounts_receivable.total > 0 && (
                      <Fragment key="ar">
                        <tr className="bg-[var(--hover)]">
                          <td className="py-2 px-5 text-xs uppercase tracking-wider text-[var(--muted-text)]">Accounts Receivable</td>
                          <td data-testid={`bs-ar-total-${railCcy}`} className="py-2 px-5 text-right font-mono-data">
                            {formatCurrency(railAssets.accounts_receivable.total, railCcy)}
                          </td>
                        </tr>
                        {detailed && isBusiness && (data.assets?.accounts_receivable?.detail || [])
                          .filter((r) => r.currency === railCcy)
                          .map((r) => (
                            <tr key={`ar-${r.id}`} data-testid={`bs-ar-row-${r.id}`}
                                className="border-b border-[var(--divider)] last:border-none">
                              <td className="py-1.5 pl-10 pr-5 text-xs text-[var(--muted-text)]">
                                <span className="font-mono">{r.number || "—"}</span> · {r.customer_name || "—"}
                                <span className="ml-2 text-[10px]">{r.date}{r.due_date ? ` → due ${r.due_date}` : ""}</span>
                              </td>
                              <td className="py-1.5 px-5 text-right font-mono-data text-xs">{formatCurrency(r.balance, railCcy)}</td>
                            </tr>
                          ))}
                      </Fragment>
                    )}
                    {railAssets.inventory && railAssets.inventory.total > 0 && (
                      <Fragment key="inv-sec">
                        <tr className="bg-[var(--hover)]">
                          <td className="py-2 px-5 text-xs uppercase tracking-wider text-[var(--muted-text)]">Inventory</td>
                          <td data-testid={`bs-inventory-total-${railCcy}`} className="py-2 px-5 text-right font-mono-data">
                            {formatCurrency(railAssets.inventory.total, railCcy)}
                          </td>
                        </tr>
                        {detailed && isBusiness && (data.assets?.inventory?.detail || [])
                          .filter((r) => r.currency === railCcy)
                          .map((r) => (
                            <tr key={`inv-${r.id}`} data-testid={`bs-inv-row-${r.id}`}
                                className="border-b border-[var(--divider)] last:border-none">
                              <td className="py-1.5 pl-10 pr-5 text-xs text-[var(--muted-text)]">
                                {r.name}{r.sku ? ` · ${r.sku}` : ""}
                                <span className="ml-2 text-[10px] font-mono">qty {r.quantity_on_hand} × {formatCurrency(r.unit_cost, railCcy)}</span>
                              </td>
                              <td className="py-1.5 px-5 text-right font-mono-data text-xs">{formatCurrency(r.value, railCcy)}</td>
                            </tr>
                          ))}
                      </Fragment>
                    )}
                    {railAssets.prepaid_tax && railAssets.prepaid_tax.total > 0 && (
                      <tr className="bg-[var(--hover)]">
                        <td className="py-2 px-5 text-xs uppercase tracking-wider text-[var(--muted-text)]">Prepaid Tax</td>
                        <td className="py-2 px-5 text-right font-mono-data">{formatCurrency(railAssets.prepaid_tax.total, railCcy)}</td>
                      </tr>
                    )}
                    {railAssets.fixed_assets && railAssets.fixed_assets.total > 0 && (
                      <Fragment key="fa">
                        <tr className="bg-[var(--hover)]">
                          <td className="py-2 px-5 text-xs uppercase tracking-wider text-[var(--muted-text)]">Fixed Assets (net)</td>
                          <td data-testid={`bs-fa-total-${railCcy}`} className="py-2 px-5 text-right font-mono-data">
                            {formatCurrency(railAssets.fixed_assets.total, railCcy)}
                          </td>
                        </tr>
                        {detailed && isBusiness && (data.assets?.fixed_assets?.lines || [])
                          .filter((r) => r.currency === railCcy)
                          .map((r) => (
                            <tr key={`fa-${r.id}`} data-testid={`bs-fa-row-${r.id}`}
                                className="border-b border-[var(--divider)] last:border-none">
                              <td className="py-1.5 pl-10 pr-5 text-xs text-[var(--muted-text)]">
                                {r.name}
                                <span className="ml-2 text-[10px] font-mono">
                                  cost {formatCurrency(r.cost, railCcy)} − depr {formatCurrency(r.accumulated_depreciation, railCcy)}
                                </span>
                              </td>
                              <td className="py-1.5 px-5 text-right font-mono-data text-xs">{formatCurrency(r.book_value, railCcy)}</td>
                            </tr>
                          ))}
                      </Fragment>
                    )}
                    <tr className="border-t-2 border-[#2F4F4F] font-semibold">
                      <td data-testid="bs-total-assets-label" className="py-3 px-5">Total assets</td>
                      <td data-testid="bs-total-assets" className="py-3 px-5 text-right font-mono-data">{formatCurrency(railAssets.total, railCcy)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="space-y-4">
                {/* iter 72ag — Detailed AP block (bill-by-bill) surfaces
                    with the Detailed toggle in Business workspaces. */}
                {(() => {
                  const apRows = (data.liabilities?.accounts_payable_detail || []).filter((r) => r.currency === railCcy);
                  return isBusiness && detailed && apRows.length > 0 && (
                  <div data-testid="bs-ap-detail" className="card-flat overflow-hidden">
                    <div className="px-5 py-3 border-b border-[var(--divider)] text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">
                      Liabilities · Accounts Payable (open bills)
                    </div>
                    <table className="w-full text-sm">
                      <tbody>
                        {apRows.map((r) => (
                            <tr key={`ap-${r.id}`} data-testid={`bs-ap-row-${r.id}`}
                                className="border-b border-[var(--divider)] last:border-none">
                              <td className="py-1.5 px-5 text-xs text-[var(--muted-text)]">
                                <span className="font-mono">{r.number || "—"}</span> · {r.vendor_name || "—"}
                                <span className="ml-2 text-[10px]">{r.date}{r.due_date ? ` → due ${r.due_date}` : ""}</span>
                              </td>
                              <td className="py-1.5 px-5 text-right font-mono-data text-xs">{formatCurrency(r.balance, railCcy)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  );
                })()}
                <LiabilitiesManager currency={railCcy} onChange={load} />
                <div className="card-flat overflow-hidden">
                  <div className="px-5 py-3 border-b border-[var(--divider)] text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Equity</div>
                  <table className="w-full text-sm">
                    <tbody>
                      {row("Owner's capital (opening)", railEquity.owners_capital, railCcy)}
                      {row("Retained earnings", railEquity.retained_earnings, railCcy)}
                      {railEquity.unrealized_investment_gains !== 0 && row("Unrealized investment gains", railEquity.unrealized_investment_gains, railCcy)}
                      {railEquity.investment_capital_introduced !== 0 && row("Investment capital introduced", railEquity.investment_capital_introduced, railCcy)}
                      {railEquity.borrowed_capital_offset !== 0 && row("Less: borrowed capital", railEquity.borrowed_capital_offset, railCcy)}
                      <tr className="border-t-2 border-[#2F4F4F] font-semibold">
                        <td className="py-3 px-5">Total equity</td>
                        <td data-testid="bs-total-equity" className="py-3 px-5 text-right font-mono-data">{formatCurrency(railEquity.total, railCcy)}</td>
                      </tr>
                      <tr className="bg-[var(--hover)] text-xs text-[var(--muted-text)]">
                        <td className="py-2 px-5">Liabilities + Equity check</td>
                        <td className="py-2 px-5 text-right font-mono-data">{formatCurrency(railLiab.total + railEquity.total, railCcy)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()
      )}
      <DrillDownModal
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        params={drill?.params}
        currency={currency}
      />
    </div>
  );
}
